import { apiClient } from "./client";
import type {
  PricingProposal,
  PricingProposalWithVersion,
  PricingProposalsPaginated,
  PricingProposalUpdate,
  PricingProposalStatusUpdate,
  ProposalVersion,
  ProposalVersionCreate,
  ProposalVersionsPaginated,
  ProposalStatus,
  ProposalAdminPaginated,
  MessageResponse,
  TemplatePage,
} from "@/types";

// ============== Proposals ==============

// Create proposal from simulation
export async function createProposalFromSimulation(
  simulationId: number,
  name: string,
  templateId?: number
): Promise<PricingProposal> {
  const params: { name: string; template_id?: number } = { name };
  if (templateId) {
    params.template_id = templateId;
  }
  const response = await apiClient.post<PricingProposal>(
    `/simulations/${simulationId}/proposals`,
    null,
    { params }
  );
  return response.data;
}

// List proposals
interface ListProposalsParams {
  page?: number;
  page_size?: number;
  status?: ProposalStatus;
  search?: string;
  archived?: boolean;
  folder_id?: number;
  simulator_id?: number;
  agency_id?: number;
  date_from?: string;
  date_to?: string;
}

export async function listProposals(
  params: ListProposalsParams = {}
): Promise<PricingProposalsPaginated> {
  const response = await apiClient.get<PricingProposalsPaginated>(
    "/proposals",
    { params }
  );
  return response.data;
}

// Get proposal by ID
export async function getProposal(
  proposalId: number
): Promise<PricingProposalWithVersion> {
  const response = await apiClient.get<PricingProposalWithVersion>(
    `/proposals/${proposalId}`
  );
  return response.data;
}

// Update proposal (creates new version)
export async function updateProposal(
  proposalId: number,
  data: ProposalVersionCreate
): Promise<PricingProposalWithVersion> {
  const response = await apiClient.put<PricingProposalWithVersion>(
    `/proposals/${proposalId}`,
    data
  );
  return response.data;
}

// Update proposal metadata
export async function updateProposalMetadata(
  proposalId: number,
  data: PricingProposalUpdate
): Promise<PricingProposal> {
  const response = await apiClient.patch<PricingProposal>(
    `/proposals/${proposalId}`,
    data
  );
  return response.data;
}

// Update proposal status
export async function updateProposalStatus(
  proposalId: number,
  data: PricingProposalStatusUpdate
): Promise<PricingProposal> {
  const response = await apiClient.patch<PricingProposal>(
    `/proposals/${proposalId}/status`,
    data
  );
  return response.data;
}

// Delete proposal
export async function deleteProposal(
  proposalId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/proposals/${proposalId}`
  );
  return response.data;
}

// Archive proposal
export async function archiveProposal(
  proposalId: number
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/proposals/${proposalId}/archive`
  );
  return response.data;
}

// Unarchive (restore) proposal
export async function unarchiveProposal(
  proposalId: number
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/proposals/${proposalId}/unarchive`
  );
  return response.data;
}

// ============== Versions ==============

// List proposal versions
interface ListVersionsParams {
  page?: number;
  page_size?: number;
}

export async function listProposalVersions(
  proposalId: number,
  params: ListVersionsParams = {}
): Promise<ProposalVersionsPaginated> {
  const response = await apiClient.get<ProposalVersionsPaginated>(
    `/proposals/${proposalId}/versions`,
    { params }
  );
  return response.data;
}

// Get specific version
export async function getProposalVersion(
  proposalId: number,
  versionId: number
): Promise<ProposalVersion> {
  const response = await apiClient.get<ProposalVersion>(
    `/proposals/${proposalId}/versions/${versionId}`
  );
  return response.data;
}

// Restore a version
export async function restoreProposalVersion(
  proposalId: number,
  versionId: number
): Promise<PricingProposalWithVersion> {
  const response = await apiClient.post<PricingProposalWithVersion>(
    `/proposals/${proposalId}/versions/${versionId}/restore`
  );
  return response.data;
}

// ============== Export ==============

// Get export data (for frontend PDF generation)
export async function getProposalExportData(
  proposalId: number
): Promise<{
  proposal_id: number;
  proposal_name: string;
  version_number: number;
  pages_data: TemplatePage[];
  editable_values: Record<string, unknown>;
}> {
  const response = await apiClient.get(
    `/proposals/${proposalId}/export/pdf`
  );
  return response.data;
}

// Download PPTX directly from stored proposal data (with coordinate adjustment)
export async function downloadProposalPptx(
  proposalId: number
): Promise<Blob> {
  const response = await apiClient.get(
    `/proposals/${proposalId}/download-pptx`,
    { responseType: "blob" }
  );
  return response.data;
}

// Export proposal as PPTX (server-side generation with native elements)
export async function exportProposalPptx(
  proposalId: number,
  pagesData: TemplatePage[],
  canvasWidth: number = 960,
  canvasHeight: number = 540
): Promise<Blob> {
  const response = await apiClient.post(
    `/proposals/${proposalId}/export/pptx`,
    {
      pages_data: pagesData,
      canvas_width: canvasWidth,
      canvas_height: canvasHeight,
    },
    { responseType: "blob" }
  );
  return response.data;
}

// ============== Admin Proposals ==============

interface ListAllProposalsAdminParams {
  page?: number;
  page_size?: number;
  simulator_id?: number;
  user_id?: number;
  status?: ProposalStatus;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export async function listAllProposalsAdmin(
  params: ListAllProposalsAdminParams = {}
): Promise<ProposalAdminPaginated> {
  const response = await apiClient.get<ProposalAdminPaginated>(
    "/admin/proposals",
    { params }
  );
  return response.data;
}
